import { createAsyncThunk } from '@reduxjs/toolkit';
import { ScimSchemaService } from '../services/ScimSchemaService';
import { combineSchemaData, extractFieldOptions } from './schemaUtils';
import { AppDispatch, RootState } from './store';
import { debugLog } from '../utils/debugLog';

/**
 * Helper function to sleep for a specified number of milliseconds
 */
const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const loadSchemaData = createAsyncThunk(
    'schema/loadSchemaData',
    async (_, { rejectWithValue }) => {
        const schemaService = new ScimSchemaService();
        
        // Load schema attributes first
        let schemaAttributesResponse;
        try {
            schemaAttributesResponse = await schemaService.getSchemaAttributes();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load schema attributes';
            console.error('❌ Redux Store: Error loading schema attributes:', error);
            return rejectWithValue(errorMessage);
        }

        // Load schema entities with retry logic
        let schemaEntitiesResponse;
        let lastError: Error | null = null;
        const maxRetries = 3;
        const retryDelayMs = 1500; // 1.5 seconds

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                schemaEntitiesResponse = await schemaService.getSchemaEntities();
                break; // Success, exit retry loop
            } catch (error) {
                lastError = error instanceof Error ? error : new Error('Unknown error');
                console.error(`❌ Redux Store: Error loading schema entities (attempt ${attempt}/${maxRetries}):`, error);
                
                // If this is not the last attempt, wait before retrying
                if (attempt < maxRetries) {
                    debugLog(`⏳ Redux Store: Waiting ${retryDelayMs}ms before retrying schema entity fetch...`);
                    await sleep(retryDelayMs);
                }
            }
        }

        // If all retries failed, reject
        if (!schemaEntitiesResponse) {
            const errorMessage = lastError?.message || 'Failed to load schema entities after retries';
            console.error('❌ Redux Store: Failed to load schema entities after all retries');
            return rejectWithValue(errorMessage);
        }

        try {
            // Combine the data into a unified schema structure
            const processedSchemas = combineSchemaData(schemaEntitiesResponse, schemaAttributesResponse);

            // Extract field options from schemas
            const fieldOptions = extractFieldOptions(processedSchemas);

            debugLog('✅ Redux Store: Schemas loaded successfully', {
                schemaCount: processedSchemas.length,
                fieldOptionCount: fieldOptions.length
            });

            return {
                entities: schemaEntitiesResponse.Resources || [],
                attributes: schemaAttributesResponse.Resources || [],
                processedSchemas,
                fieldOptions
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to process schemas';
            console.error('❌ Redux Store: Error processing schemas:', error);
            return rejectWithValue(errorMessage);
        }
    }
);

